import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import WeatherWidget from "@/components/custom/WeatherWidget";
import MunicipalitySelector from "@/components/custom/MunicipalitySelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Siren, ShieldAlert, AlertTriangle, MapPin, Clock, Navigation,
  CheckCircle, XCircle, HelpCircle, Package, Users, Truck, Home, Stethoscope,
  Plus, Edit3, ShieldCheck, ShieldX,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#686af3", color = "#fff" }) {
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

function BBtn({ children, onClick, disabled, variant = "primary", className = "", testId, style = {} }) {
  const styles = {
    primary: { background: "#f64e0e", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    secondary: { background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    ghost: { background: "transparent", color: "#2D3561", border: "2px solid #ccc" },
    outline: { background: "#fff", color: "#2D3561", border: B.border },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 ${className}`}
      style={{ fontFamily: B.mono, ...styles[variant], ...style }}
    >
      {children}
    </button>
  );
}

const EMERGENCY_TYPES = [
  { value: "dana", label: "DANA", icon: "" },
  { value: "inundacion", label: "Inundación", icon: "" },
  { value: "incendio", label: "Incendio", icon: "" },
  { value: "terremoto", label: "Terremoto", icon: "" },
  { value: "huracan", label: "Huracán/Tormenta", icon: "" },
  { value: "sequia", label: "Sequía", icon: "" },
  { value: "nevada", label: "Nevada/Ola de frío", icon: "" },
  { value: "rescate_animal", label: "Rescate de animales", icon: "" },
  { value: "otro", label: "Otro", icon: "" },
];

const NEED_TYPES = [
  { value: "material", label: "Material", icon: Package },
  { value: "voluntarios", label: "Voluntarios", icon: Users },
  { value: "transporte", label: "Transporte", icon: Truck },
  { value: "acogida", label: "Acogida", icon: Home },
  { value: "veterinario", label: "Veterinario", icon: Stethoscope },
  { value: "otro", label: "Otro", icon: HelpCircle },
];

const URGENCY_LEVELS = [
  { value: "urgente", label: "URGENTE", color: "#f64e0e" },
  { value: "prioridad", label: "PRIORIDAD", color: "#fa9000" },
  { value: "activo", label: "ACTIVO", color: "#686af3" },
  { value: "info", label: "INFO", color: "#a88efb" },
];

const CORPS_LABELS = {
  proteccion_civil: "Protección Civil",
  bomberos: "Bomberos",
  policia_nacional: "Policía Nacional",
  policia_local: "Policía Local",
  guardia_civil: "Guardia Civil",
  mossos: "Mossos d'Esquadra",
  ertzaintza: "Ertzaintza",
  policia_foral: "Policía Foral",
  agents_rurals: "Agents Rurals / Forestales",
  sanitarios: "Sanitarios / SAMUR",
  cruz_roja: "Cruz Roja",
  veterinarios: "Veterinarios de Emergencias",
  otro: "Otro",
};

const dialogStyle = { borderRadius: 0, border: B.border, boxShadow: B.shadow };
const inputStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const selectStyle = { border: "2px solid #2D3561", borderRadius: 0 };

export default function EmergencyPersonnelDashboard() {
  const { user } = useAuth();
  const [emergencies, setEmergencies] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [showNewEmergency, setShowNewEmergency] = useState(false);
  const [showNewNeed, setShowNewNeed] = useState(false);
  const [emergencyForm, setEmergencyForm] = useState({
    type: "rescate_animal", title: "", description: "", location_name: "", location_lat: null, location_lng: null,
  });
  const [needForm, setNeedForm] = useState({
    emergency_id: "", type: "material", title: "", description: "", quantity: "", urgency: "activo",
  });
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  const isVerified = user?.emergency_verified;
  const isPending = user?.emergency_verification_pending;
  const corpsLabel = CORPS_LABELS[user?.emergency_corps] || "Personal de Emergencias";

  const fetchData = useCallback(() => {
    if (!user || !isVerified) return;
    axios.get(`${API}/shelters/${user.user_id}/emergencies`, { headers, withCredentials: true })
      .then((r) => setEmergencies(r.data))
      .catch(() => {});
    axios.get(`${API}/needs?status=abierta`)
      .then((r) => {
        const myNeeds = r.data.filter((n) => n.shelter_id === user.user_id);
        setNeeds(myNeeds);
      })
      .catch(() => {});
  }, [user, isVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateEmergency = async () => {
    if (!emergencyForm.title.trim()) { toast.error("El título es obligatorio"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/emergencies`, emergencyForm, { headers, withCredentials: true });
      toast.success("Alerta creada");
      setShowNewEmergency(false);
      setEmergencyForm({ type: "rescate_animal", title: "", description: "", location_name: "", location_lat: null, location_lng: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear alerta");
    } finally { setLoading(false); }
  };

  const handleCreateNeed = async () => {
    if (!needForm.title.trim() || !needForm.emergency_id) {
      toast.error("Selecciona una alerta y escribe un título");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/needs`, needForm, { headers, withCredentials: true });
      toast.success("Necesidad publicada");
      setShowNewNeed(false);
      setNeedForm({ emergency_id: "", type: "material", title: "", description: "", quantity: "", urgency: "activo" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear necesidad");
    } finally { setLoading(false); }
  };

  const activeEmergencies = emergencies.filter((e) => e.status === "activo");

  if (!user) return null;

  // Pending verification view
  if (isPending || !isVerified) {
    return (
      <div className="min-h-screen" data-testid="emergency-dashboard-pending" style={{ background: "#f8f7f4", fontFamily: B.font }}>
        <Navbar />
        <div className="pt-28 pb-16 max-w-2xl mx-auto px-6 text-center">
          <BCard className="p-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ background: isPending ? "#fa9000" : "#e8637a", border: B.border }}>
              {isPending ? <ShieldCheck className="w-8 h-8 text-white" /> : <ShieldX className="w-8 h-8 text-white" />}
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ color: "#2D3561" }}>
              {isPending ? "VERIFICACIÓN PENDIENTE" : "VERIFICACIÓN REQUERIDA"}
            </h1>
            <p className="text-sm mb-4" style={{ color: "#666" }}>
              {isPending 
                ? "Tu cuenta de Personal de Emergencias está siendo revisada. Recibirás una notificación cuando esté aprobada."
                : user?.emergency_rejection_reason 
                  ? `Tu solicitud no fue aprobada: ${user.emergency_rejection_reason}`
                  : "Tu cuenta requiere verificación para poder publicar alertas."
              }
            </p>
            <div className="p-4 mt-4" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
              <p className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>TU PERFIL</p>
              <p className="text-sm font-bold mt-1" style={{ color: "#2D3561" }}>{user.name}</p>
              <p className="text-xs" style={{ fontFamily: B.mono, color: "#999" }}>{corpsLabel}</p>
            </div>
          </BCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="emergency-dashboard" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      <Navbar />

      <div className="pt-28 pb-16 max-w-7xl mx-auto px-6 md:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-8">
          {/* Profile Card */}
          <BCard className="md:col-span-5 p-5">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                <Siren className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold" style={{ color: "#2D3561" }}>{user.name}</h2>
                <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#f64e0e" }}>{corpsLabel.toUpperCase()}</p>
                <p className="text-[10px] tracking-widest mt-0.5" style={{ fontFamily: B.mono, color: "#a88efb" }}>{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <BBadge bg="#f64e0e">PERSONAL EMERGENCIAS</BBadge>
              <BBadge bg="#8B9A2D">VERIFICADO</BBadge>
            </div>
            <p className="text-[10px] mt-3" style={{ fontFamily: B.mono, color: "#999" }}>
              Tus publicaciones aparecen como: {user.emergency_display_mode === "anonymous" ? '"Personal de Emergencias"' : `"Personal de Emergencias - ${corpsLabel}"`}
            </p>
          </BCard>

          {/* Stats */}
          <div className="md:col-span-3 grid grid-cols-2 gap-4">
            <BCard className="p-4 text-center" style={{ boxShadow: B.shadowSm }}>
              <AlertTriangle className="w-5 h-5 mx-auto mb-2" style={{ color: "#f64e0e" }} />
              <p className="text-2xl font-bold" style={{ color: "#2D3561" }}>{activeEmergencies.length}</p>
              <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: "#f64e0e" }}>ALERTAS</p>
            </BCard>
            <BCard className="p-4 text-center" style={{ boxShadow: B.shadowSm }}>
              <Package className="w-5 h-5 mx-auto mb-2" style={{ color: "#686af3" }} />
              <p className="text-2xl font-bold" style={{ color: "#2D3561" }}>{needs.length}</p>
              <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: "#686af3" }}>NECESIDADES</p>
            </BCard>
          </div>

          {/* Weather */}
          <div className="md:col-span-4">
            <WeatherWidget compact userLat={user?.location_lat} userLng={user?.location_lng} userName={user?.location_name} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-8">
          <BBtn onClick={() => setShowNewEmergency(true)} variant="primary" testId="create-alert-btn">
            <AlertTriangle className="w-4 h-4" />
            NUEVA ALERTA
          </BBtn>
          <BBtn
            onClick={() => setShowNewNeed(true)}
            disabled={activeEmergencies.length === 0}
            variant="secondary"
            testId="create-need-btn"
          >
            <Plus className="w-4 h-4" />
            PUBLICAR NECESIDAD
          </BBtn>
        </div>

        {/* Emergencies List */}
        <div className="mb-8">
          <h3 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
            MIS ALERTAS
          </h3>
          {emergencies.length > 0 ? (
            <div className="space-y-4">
              {emergencies.map((emg) => (
                <BCard key={emg.emergency_id} className="p-5" data-testid={`alert-${emg.emergency_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <BBadge bg={emg.status === "activo" ? "#f64e0e" : "#999"}>{emg.type}</BBadge>
                        <BBadge bg={emg.status === "activo" ? "#8B9A2D" : "#ccc"}>{emg.status}</BBadge>
                      </div>
                      <h4 className="text-base font-bold" style={{ color: "#2D3561" }}>{emg.title}</h4>
                      <p className="text-sm mt-1" style={{ color: "#666" }}>{emg.description}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="flex items-center gap-1 text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                          <MapPin className="w-3 h-3" /> {emg.location_name || "Sin ubicación"}
                        </span>
                        <span className="flex items-center gap-1 text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                          <Clock className="w-3 h-3" /> {new Date(emg.created_at).toLocaleDateString("es-ES")}
                        </span>
                      </div>
                    </div>
                  </div>
                </BCard>
              ))}
            </div>
          ) : (
            <BCard className="p-8 text-center">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: "#ccc" }} />
              <p className="text-sm" style={{ color: "#999" }}>No tienes alertas registradas</p>
            </BCard>
          )}
        </div>

        {/* Needs List */}
        {needs.length > 0 && (
          <div>
            <h3 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
                <Package className="w-4 h-4 text-white" />
              </div>
              MIS NECESIDADES
            </h3>
            <div className="space-y-4">
              {needs.map((need) => {
                const urgencyColor = URGENCY_LEVELS.find((u) => u.value === need.urgency)?.color || "#686af3";
                return (
                  <BCard key={need.need_id} className="p-5" data-testid={`need-${need.need_id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <BBadge bg={urgencyColor}>{need.urgency}</BBadge>
                      <BBadge bg="#2D3561">{need.type}</BBadge>
                    </div>
                    <h4 className="text-base font-bold" style={{ color: "#2D3561" }}>{need.title}</h4>
                    <p className="text-sm mt-1" style={{ color: "#666" }}>{need.description}</p>
                    <p className="text-[10px] mt-2 tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                      {need.offers_count || 0} ofertas recibidas
                    </p>
                  </BCard>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* New Alert Dialog */}
      <Dialog open={showNewEmergency} onOpenChange={setShowNewEmergency}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              NUEVA ALERTA
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px" }}>
              Registra una situación de emergencia o rescate
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO</Label>
              <Select value={emergencyForm.type} onValueChange={(v) => setEmergencyForm({ ...emergencyForm, type: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="alert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]" style={{ borderRadius: 0, border: B.border }}>
                  {EMERGENCY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">{t.icon} {t.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={emergencyForm.title}
                onChange={(e) => setEmergencyForm({ ...emergencyForm, title: e.target.value })}
                placeholder="Ej: Rescate de 5 perros en zona inundada"
                className="mt-1"
                style={inputStyle}
                data-testid="alert-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={emergencyForm.description}
                onChange={(e) => setEmergencyForm({ ...emergencyForm, description: e.target.value })}
                placeholder="Describe la situación..."
                className="mt-1"
                style={inputStyle}
                data-testid="alert-description-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>UBICACIÓN</Label>
              <div className="flex gap-2 mt-1">
                <MunicipalitySelector
                  value={emergencyForm.location_name}
                  onChange={(name) => setEmergencyForm({ ...emergencyForm, location_name: name })}
                  onLocationSelect={(lat, lng, name) => {
                    setEmergencyForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name }));
                    toast.success(`${name} seleccionado`);
                  }}
                  placeholder="Buscar municipio..."
                  className="flex-1"
                />
                <button
                  type="button"
                  disabled={locating}
                  onClick={() => {
                    if (!navigator.geolocation) return;
                    setLocating(true);
                    navigator.geolocation.getCurrentPosition(
                      async (pos) => {
                        const lat = pos.coords.latitude;
                        const lng = pos.coords.longitude;
                        let name = "";
                        try {
                          const geo = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`);
                          const addr = geo.data?.address;
                          name = addr?.city || addr?.town || addr?.village || addr?.state || "";
                        } catch {}
                        setEmergencyForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name || f.location_name }));
                        setLocating(false);
                        toast.success(name ? `${name}` : "Coordenadas guardadas");
                      },
                      () => { setLocating(false); toast.error("No se pudo obtener ubicación"); },
                      { enableHighAccuracy: true, timeout: 10000 }
                    );
                  }}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#f64e0e10] transition-colors"
                  style={{ border: "2px solid #f64e0e" }}
                  data-testid="alert-geolocate-btn"
                >
                  <Navigation className={`w-4 h-4 ${locating ? "animate-spin" : ""}`} style={{ color: "#f64e0e" }} />
                </button>
              </div>
            </div>
            <button
              onClick={handleCreateEmergency}
              disabled={loading}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
              style={{ fontFamily: B.font, background: "#f64e0e", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
              data-testid="submit-alert-btn"
            >
              {loading ? "CREANDO..." : "REGISTRAR ALERTA"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Need Dialog */}
      <Dialog open={showNewNeed} onOpenChange={setShowNewNeed}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
                <Package className="w-4 h-4 text-white" />
              </div>
              PUBLICAR NECESIDAD
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>ALERTA ASOCIADA</Label>
              <Select value={needForm.emergency_id} onValueChange={(v) => setNeedForm({ ...needForm, emergency_id: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="need-alert-select">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 0, border: B.border }}>
                  {activeEmergencies.map((e) => (
                    <SelectItem key={e.emergency_id} value={e.emergency_id}>{e.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO</Label>
              <Select value={needForm.type} onValueChange={(v) => setNeedForm({ ...needForm, type: v })}>
                <SelectTrigger className="mt-1" style={selectStyle}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 0, border: B.border }}>
                  {NEED_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={needForm.title}
                onChange={(e) => setNeedForm({ ...needForm, title: e.target.value })}
                placeholder="Ej: Necesitamos jaulas de transporte"
                className="mt-1"
                style={inputStyle}
                data-testid="need-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={needForm.description}
                onChange={(e) => setNeedForm({ ...needForm, description: e.target.value })}
                placeholder="Detalla la necesidad..."
                className="mt-1"
                style={inputStyle}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CANTIDAD</Label>
                <Input
                  value={needForm.quantity}
                  onChange={(e) => setNeedForm({ ...needForm, quantity: e.target.value })}
                  placeholder="Ej: 10 unidades"
                  className="mt-1"
                  style={inputStyle}
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>URGENCIA</Label>
                <Select value={needForm.urgency} onValueChange={(v) => setNeedForm({ ...needForm, urgency: v })}>
                  <SelectTrigger className="mt-1" style={selectStyle}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ borderRadius: 0, border: B.border }}>
                    {URGENCY_LEVELS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2" style={{ backgroundColor: u.color }} />
                          {u.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button
              onClick={handleCreateNeed}
              disabled={loading}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
              style={{ fontFamily: B.font, background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
              data-testid="submit-need-btn"
            >
              {loading ? "PUBLICANDO..." : "PUBLICAR NECESIDAD"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
